/**
 * Says how much of an answer a list is, when the answer is less than all of it.
 *
 * Every list the API returns carries a `result` state: complete, partial, or
 * unavailable. This renders the last two and nothing else, because a complete
 * answer needs no explanation.
 *
 * It exists as one component rather than a banner per page because the failure
 * it describes used to be invisible everywhere at once — a provider timeout, a
 * rate limit and a genuinely empty list all arrived as an empty list, and the
 * user was told the same thing in all three cases. One component means one
 * wording, and means a new list cannot quietly go back to saying nothing.
 */

import { Alert } from "@norican/react";

import { useT } from "@/i18n";

import { isRequesterUnresolvedResult } from "./api-error";
import { RequesterUnresolvedNotice } from "./requester-unresolved-notice";

/**
 * The shape every list response carries.
 *
 * Structural and permissive on purpose: the generated client types both fields
 * as optional because the server defaults them, and a component that refused
 * the generated shape would just get cast around at every call site.
 */
export interface ListResult {
  state?: string | null;
  reason?: string | null;
}

export interface ResultStateNoticeProps {
  result: ListResult | null | undefined;
  className?: string;
}

export function ResultStateNotice({ result, className }: ResultStateNoticeProps) {
  const { t } = useT("common");

  // An absent state means the server said nothing was wrong.
  if (!result || !result.state || result.state === "COMPLETE") {
    return null;
  }

  // Ivanti has no record of this person. Whether nothing could be read
  // (UNAVAILABLE) or only what matched by email (PARTIAL), the fix is the same
  // and it is not "try again": say who to contact.
  if (isRequesterUnresolvedResult(result)) {
    return <RequesterUnresolvedNotice className={className} />;
  }

  if (result.state === "UNAVAILABLE") {
    // Not "you have none". We could not ask.
    return (
      <Alert tone="warning" title={t("result.unavailableTitle")} className={className}>
        {t("result.unavailableBody")}
      </Alert>
    );
  }

  // PARTIAL — the rows shown are real; the ones missing are not evidence.
  const body =
    result.reason === "PROVIDER_PAGE_LIMIT" ? t("result.pageLimitBody") : t("result.partialBody");
  return (
    <Alert tone="info" title={t("result.partialTitle")} className={className}>
      {body}
    </Alert>
  );
}
